/**
 * ProfileEditModal — self-serve profile editor available to ALL tiers.
 *
 * Handles two things:
 *   1. Phone number  — add / edit / delete.
 *   2. Avatar image  — pick a JPG/PNG/WEBP; we downscale to 256×256
 *      JPEG on the client (canvas.toDataURL, 0.85 quality) before
 *      uploading, keeping the server payload well under the 300 KB cap.
 *
 * On save we call the auth context's `refresh()` so the new avatar
 * flows into every `UserAvatar` instance mounted across the app in
 * real time.
 */
import { useEffect, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, X, User } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import { COUNTRIES } from "@/data/phoneCountries";

const AVATAR_SIZE_PX = 256;
const AVATAR_QUALITY = 0.85;

/** Client-side downscale & recompress to a JPEG data URL. */
async function fileToDataUrl(file) {
    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve; img.onerror = reject; img.src = dataUrl;
    });
    // Square-crop from the centre, then downscale.
    const side = Math.min(img.width, img.height);
    const sx = (img.width - side) / 2;
    const sy = (img.height - side) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_SIZE_PX;
    canvas.height = AVATAR_SIZE_PX;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_SIZE_PX, AVATAR_SIZE_PX);
    return canvas.toDataURL("image/jpeg", AVATAR_QUALITY);
}

export default function ProfileEditModal({ open, onClose }) {
    const { user, refresh } = useAuth();
    const [phone, setPhone] = useState("");
    const [fullName, setFullName] = useState("");
    const [nationality, setNationality] = useState("");
    const [countryOfResidence, setCountryOfResidence] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [removingAvatar, setRemovingAvatar] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setPhone(user?.phone || "");
            setFullName(user?.full_name || "");
            setNationality(user?.nationality || "");
            setCountryOfResidence(user?.country_of_residence || "");
            setPreviewUrl(null);
        }
    }, [open, user]);

    const dirty =
        phone.trim() !== (user?.phone || "").trim()
        || fullName.trim() !== (user?.full_name || "").trim()
        || nationality !== (user?.nationality || "")
        || countryOfResidence !== (user?.country_of_residence || "");

    async function saveProfile() {
        setSaving(true);
        try {
            const patch = {};
            // Only send fields that actually changed to avoid unnecessary writes.
            if (fullName.trim() !== (user?.full_name || "").trim()) patch.full_name = fullName.trim();
            if (phone !== (user?.phone || "")) patch.phone = phone;  // empty string = delete
            if (nationality !== (user?.nationality || "")) patch.nationality = nationality;
            if (countryOfResidence !== (user?.country_of_residence || "")) patch.country_of_residence = countryOfResidence;
            await api.patch("/me/profile", patch);
            await refresh();
            toast.success("Profile updated");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    }

    async function onFilePicked(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/^image\/(jpe?g|png|webp)$/.test(file.type)) {
            toast.error("Pick a JPG, PNG, or WEBP image.");
            return;
        }
        setUploading(true);
        try {
            const dataUrl = await fileToDataUrl(file);
            setPreviewUrl(dataUrl);
            await api.post("/me/profile/avatar", { avatar_data_url: dataUrl });
            await refresh();
            toast.success("Profile picture updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
            setPreviewUrl(null);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    async function removeAvatar() {
        setRemovingAvatar(true);
        try {
            await api.delete("/me/profile/avatar");
            await refresh();
            setPreviewUrl(null);
            toast.success("Profile picture removed");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Remove failed");
        } finally {
            setRemovingAvatar(false);
        }
    }

    // Merge preview into the displayed user so the avatar swap feels instant.
    const displayedUser = previewUrl ? { ...user, avatar_data_url: previewUrl } : user;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="profile-edit-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-4 h-4" /> Edit your profile
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Update your name, mobile number and profile picture. Your picture
                        will show up on leaderboards, admin dashboards and anywhere your
                        account is displayed.
                    </DialogDescription>
                </DialogHeader>

                {/* Avatar row */}
                <div className="flex items-center gap-4 py-3 border-b border-border">
                    <UserAvatar user={displayedUser} size={72} testid="profile-edit-avatar-preview" />
                    <div className="flex-1">
                        <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Profile picture</div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={onFilePicked}
                                className="hidden"
                                data-testid="profile-edit-avatar-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="profile-edit-avatar-upload"
                            >
                                {uploading
                                    ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                                    : <Upload className="w-3 h-3 mr-1.5" />}
                                Upload picture
                            </Button>
                            {user?.avatar_data_url && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={removeAvatar}
                                    disabled={removingAvatar}
                                    data-testid="profile-edit-avatar-remove"
                                    className="text-danger border-danger/40 hover:bg-danger hover:text-white"
                                >
                                    {removingAvatar
                                        ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                                        : <Trash2 className="w-3 h-3 mr-1.5" />}
                                    Remove
                                </Button>
                            )}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            JPG, PNG or WEBP. We crop to a square and downscale to
                            256 × 256 before saving.
                        </div>
                    </div>
                </div>

                {/* Name */}
                <label className="block">
                    <span className="text-[10px] uppercase text-muted-foreground tracking-wider">Full name</span>
                    <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        maxLength={120}
                        placeholder="e.g. Wanjiku Njoroge"
                        data-testid="profile-edit-name"
                        className="w-full mt-1 px-3 py-2 rounded border border-border bg-background text-sm"
                    />
                </label>

                {/* Phone */}
                <label className="block">
                    <span className="text-[10px] uppercase text-muted-foreground tracking-wider">Mobile number</span>
                    <div className="flex gap-2 mt-1">
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+254 7XX XXX XXX"
                            data-testid="profile-edit-phone"
                            className="flex-1 px-3 py-2 rounded border border-border bg-background text-sm"
                        />
                        {phone && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setPhone("")}
                                data-testid="profile-edit-phone-clear"
                                className="text-danger border-danger/40"
                                title="Delete phone"
                            >
                                <Trash2 className="w-3 h-3" />
                            </Button>
                        )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                        Add or update to receive in-app SMS-style notifications. Leave empty
                        and click the trash icon to remove the number entirely.
                    </div>
                </label>

                {/* Nationality + Country of residence — required for every account
                    (Google sign-ups skip these at consent time; fill them in here). */}
                <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                        <span className="text-[10px] uppercase text-muted-foreground tracking-wider">Nationality</span>
                        <Select value={nationality} onValueChange={setNationality}>
                            <SelectTrigger className="w-full mt-1 h-10 text-sm" data-testid="profile-edit-nationality">
                                <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {COUNTRIES.map((c) => (
                                    <SelectItem key={c.iso2} value={c.name} data-testid={`profile-edit-nationality-${c.iso2}`}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase text-muted-foreground tracking-wider">Country of residence</span>
                        <Select value={countryOfResidence} onValueChange={setCountryOfResidence}>
                            <SelectTrigger className="w-full mt-1 h-10 text-sm" data-testid="profile-edit-country-residence">
                                <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {COUNTRIES.map((c) => (
                                    <SelectItem key={c.iso2} value={c.name} data-testid={`profile-edit-residence-${c.iso2}`}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </label>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                    <DialogClose asChild>
                        <Button type="button" variant="outline" size="sm" data-testid="profile-edit-cancel">
                            <X className="w-3 h-3 mr-1.5" /> Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="button"
                        size="sm"
                        onClick={saveProfile}
                        disabled={saving || !dirty}
                        data-testid="profile-edit-save"
                    >
                        {saving && <Loader2 className="w-3 h-3 animate-spin mr-1.5" />}
                        Save changes
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
